import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { uploadTempFileRequest } from "../../../../../../utils/APICalls";

type Props = {
  formik: any;
};

interface AttachmentFile {
  id: string;
  name: string;
  size: string;
  isUploaded: boolean;
  fileKey?: string;
  isUploading?: boolean;
}

const Attachments = (props: Props) => {
  const { formik } = props;
  const [attachments, setAttachments] = useState<AttachmentFile[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<{ [key: string]: File }>(
    {}
  );
  const fileInputRef = useRef<HTMLInputElement>(null);
  const skipFormikToLocalRef = useRef(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [attachmentToDeleteId, setAttachmentToDeleteId] = useState<
    string | null
  >(null);
  const [uploadError, setUploadError] = useState(false);
  const [uploadErrorMessage, setUploadErrorMessage] = useState("");

  useEffect(() => {
    if (skipFormikToLocalRef.current) {
      skipFormikToLocalRef.current = false;
      return;
    }
    if (
      formik.values.attachments &&
      formik.values.attachments.length > 0 &&
      attachments.length === 0
    ) {
      const mappedAttachments: AttachmentFile[] = formik.values.attachments.map(
        (att: any) => ({
          id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
          name: att.attachmentName || "Unknown File",
          size: att.size || "Unknown",
          isUploaded: true,
          fileKey: att.attachmentKey,
        })
      );
      setAttachments(mappedAttachments);
    }
  }, [formik.values.attachments, attachments.length]); // eslint-disable-line

  useEffect(() => {
    const formikAttachments = attachments
      .filter((att) => att.fileKey)
      .map((att) => ({
        attachmentKey: att.fileKey,
        attachmentName: att.name,
      }));
    formik.setFieldValue("attachments", formikAttachments);
  }, [attachments]); // eslint-disable-line

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      setUploadError(false);
      setUploadErrorMessage("");

      // Validate file type
      const allowedTypes = [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "image/jpeg",
        "image/jpg",
        "image/png",
      ];

      if (!allowedTypes.includes(file.type)) {
        setUploadError(true);
        setUploadErrorMessage(
          "Unsupported file type. Only PDF, DOC, DOCX, XLSX, JPG, or PNG files are allowed."
        );
        return;
      }

      if (file.size > 5000000) {
        setUploadError(true);
        setUploadErrorMessage("File size exceeds 5MB limit.");
        return;
      }

      const newAttachment: AttachmentFile = {
        id: Date.now().toString(),
        name: file.name,
        size: `${(file.size / 1024).toFixed(0)}kb`,
        isUploaded: false,
        isUploading: true,
      };

      setAttachments((prev) => [...prev, newAttachment]);
      setSelectedFiles((prev) => ({ ...prev, [newAttachment.id]: file }));

      try {
        const response = await uploadTempFileRequest(file);

        if (response && response.data) {
          setUploadError(false);
          setUploadErrorMessage("");
          setAttachments((prev) =>
            prev.map((att) =>
              att.id === newAttachment.id
                ? {
                    ...att,
                    isUploaded: true,
                    fileKey: response.data,
                    isUploading: false,
                  }
                : att
            )
          );
        } else {
          setAttachments((prev) =>
            prev.filter((att) => att.id !== newAttachment.id)
          );
          setSelectedFiles((prev) => {
            const newFiles = { ...prev };
            delete newFiles[newAttachment.id];
            return newFiles;
          });
          setUploadError(true);
          setUploadErrorMessage(
            response?.data?.detail ||
              "Failed to upload file. Please try again."
          );
        }
      } catch (error) {
        setAttachments((prev) =>
          prev.filter((att) => att.id !== newAttachment.id)
        );
        setSelectedFiles((prev) => {
          const newFiles = { ...prev };
          delete newFiles[newAttachment.id];
          return newFiles;
        });
        setUploadError(true);
        setUploadErrorMessage("Failed to upload file. Please try again.");
      }
    }
  };

  const handleFileDelete = (e: React.MouseEvent, attachmentId: string) => {
    e.preventDefault();
    e.stopPropagation();
    skipFormikToLocalRef.current = true;
    if (selectedFiles[attachmentId]) {
      setSelectedFiles((prev) => {
        const newFiles = { ...prev };
        delete newFiles[attachmentId];
        return newFiles;
      });
    }
    setAttachments((prev) => prev.filter((att) => att.id !== attachmentId));
  };

  const openDeleteConfirm = (e: React.MouseEvent, attachmentId: string) => {
    e.preventDefault();
    e.stopPropagation();
    setAttachmentToDeleteId(attachmentId);
    setIsDeleteModalOpen(true);
  };

  const confirmDeleteAttachment = () => {
    if (attachmentToDeleteId) {
      const syntheticEvent = {
        preventDefault: () => {},
        stopPropagation: () => {},
      } as React.MouseEvent;
      handleFileDelete(syntheticEvent, attachmentToDeleteId);
      setAttachmentToDeleteId(null);
    }
    setIsDeleteModalOpen(false);
  };

  const renderUploadArea = () => {
    return (
      <Box
        sx={{
          height: 80,
          width: "100%",
          border: "1px dashed rgba(0, 0, 0, 0.12)",
          position: "relative",
          display: "flex",
          alignItems: "center",
          borderRadius: 1,
          cursor: "pointer",
          opacity: 1,
          pointerEvents: "auto",
          backgroundColor: "transparent",
          "&:hover": {
            borderColor: "primary.main",
            backgroundColor: "action.hover",
          },
        }}
        onClick={() => fileInputRef.current?.click()}
      >
        <UploadFileIcon sx={{ ml: 5 }} color="primary" />
        <Box ml={2}>
          <Box sx={{ display: "flex" }}>
            <Typography color={"primary"} sx={{ textDecoration: "underline" }}>
              Click to upload
            </Typography>
            <Typography
              sx={{
                ml: 1,
                color: "text.primary",
              }}
            >
              or drag and drop
            </Typography>
          </Box>
          <Typography
            sx={{
              color: "rgba(0, 0, 0, 0.6)",
            }}
          >
            PDF, DOC, DOCX, XLSX, JPG, or PNG (max. 5MB)
          </Typography>
        </Box>
      </Box>
    );
  };

  const renderFileItem = (attachment: AttachmentFile) => {
    if (attachment.isUploaded) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            border: "1px solid #E0E0E0",
            p: 1,
            borderRadius: 1,
            backgroundColor: "#fff",
          }}
        >
          <Box display="flex" alignItems="center">
            <AttachFileOutlinedIcon color="primary" sx={{ mr: 1 }} />
            <Box>
              <Typography variant="body2">{attachment.name}</Typography>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                {attachment.size !== "Unknown" ? `${attachment.size} • ` : ""}
                Complete
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CheckCircleOutlineIcon color="success" sx={{ mr: 2 }} />
            <IconButton
              aria-label="delete"
              type="button"
              onClick={(e) => openDeleteConfirm(e, attachment.id)}
            >
              <DeleteOutlineIcon color="error" />
            </IconButton>
          </Box>
        </Box>
      );
    } else if (attachment.isUploading) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            border: "1px solid #E0E0E0",
            p: 1,
            borderRadius: 1,
            backgroundColor: "#fff",
          }}
        >
          <Box display="flex" alignItems="center">
            <AttachFileOutlinedIcon color="primary" sx={{ mr: 1 }} />
            <Box>
              <Typography variant="body2">{attachment.name}</Typography>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                {attachment.size} • Uploading
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton
              aria-label="delete"
              type="button"
              onClick={(e) => openDeleteConfirm(e, attachment.id)}
            >
              <RemoveCircleOutlineIcon color="error" />
            </IconButton>
          </Box>
        </Box>
      );
    } else {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            border: "1px solid #E0E0E0",
            p: 1,
            borderRadius: 1,
            backgroundColor: "#fff",
          }}
        >
          <Box display="flex" alignItems="center">
            <AttachFileOutlinedIcon color="primary" sx={{ mr: 1 }} />
            <Box>
              <Typography variant="body2">{attachment.name}</Typography>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                {attachment.size}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <IconButton
              size="small"
              type="button"
              aria-label="delete"
              onClick={(e) => openDeleteConfirm(e, attachment.id)}
            >
              <DeleteOutlineIcon color="error" fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      );
    }
  };

  return (
    <Box>
      <Box sx={{ width: "100%", display: "flex", flexDirection: "column" }}>
        <Box sx={{ display: "flex", flexDirection: "column", mt: 1 }}>
          {attachments.map((attachment) => (
            <Box
              key={attachment.id}
              sx={{
                py: 1,
              }}
            >
              {renderFileItem(attachment)}
            </Box>
          ))}
          <Box sx={{ py: 1, mt: 1 }}>
            {renderUploadArea()}
            {uploadError && (
              <Typography sx={{ fontSize: 12, mt: 0.5 }} color="error">
                {uploadErrorMessage}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
      <input
        ref={fileInputRef}
        style={{
          display: "none",
        }}
        type="file"
        accept=".pdf,.doc,.docx,.xlsx,.jpg,.jpeg,.png,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,image/jpeg,image/jpg,image/png"
        onChange={handleFileChange}
        onClick={(event) => {
          (event.target as HTMLInputElement).value = "";
        }}
      />
      <Dialog
        open={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setAttachmentToDeleteId(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6">Delete Additional Details?</Typography>
            <IconButton
              onClick={() => {
                setIsDeleteModalOpen(false);
                setAttachmentToDeleteId(null);
              }}
            >
              <Typography>×</Typography>
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1 }}>
            Are you sure you want to delete these additional details?
            <br />
            This action can&apos;t be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            variant="contained"
            onClick={() => {
              setIsDeleteModalOpen(false);
              setAttachmentToDeleteId(null);
            }}
            sx={{
              backgroundColor: "primary.main",
              color: "white",
              textTransform: "none",
              "&:hover": { backgroundColor: "primary.main" },
            }}
          >
            CANCEL
          </Button>
          <Button
            onClick={confirmDeleteAttachment}
            variant="text"
            sx={{
              color: "error.main",
              textTransform: "none",
            }}
          >
            DELETE
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Attachments;
